import type { PropsWithChildren } from 'react';
import { cloneElement, isValidElement, useState } from 'react';

import { createDivInputStyles, ErrorMessage, Label } from '@change/ds-forms';

export type StyleWrapperProps = {
	label: string;
	disabled?: boolean;
	errorMessage?: string;
	onFocus?: () => void;
	onBlur?: (args: unknown) => void;
};

export function StyleWrapper({
	children,
	label,
	disabled,
	errorMessage,
	onFocus,
	onBlur,
}: PropsWithChildren<StyleWrapperProps>): React.JSX.Element {
	const [focused, setFocused] = useState<boolean>(false);
	const [hovered, setHovered] = useState<boolean>(false);

	if (!isValidElement(children)) {
		throw new Error('Children must be a valid React element');
	}

	const handleFocus = () => {
		setFocused(true);
		onFocus?.();
	};

	const handleBlur = (args: unknown) => {
		setFocused(false);
		onBlur?.(args);
	};

	return (
		<div className="flex flex-col gap-1">
			<Label isDisabled={disabled}>
				<div className="flex flex-col gap-1">
					{label}
					{/* to associate label and input, passing input as child */}
					<div
						className={createDivInputStyles({ focused, invalid: !!errorMessage, disabled, hovered })}
						onMouseEnter={() => setHovered(true)}
						onMouseLeave={() => setHovered(false)}
					>
						{cloneElement(children, { onFocus: handleFocus, onBlur: handleBlur })}
					</div>
				</div>
			</Label>

			{errorMessage && (
				<div data-qa="field-error-message">
					<ErrorMessage size="small">{errorMessage}</ErrorMessage>
				</div>
			)}
		</div>
	);
}
